import { FC, useEffect, useState } from 'react';
import useLogRocket from '~/hooks/useLogrocket';
import Head from 'next/head';
import { AppProps } from 'next/app';
import FuelProvider, { FuelProviderCallbackProps } from '~/providers/FuelProvider';
import { ChatbotProvider } from '~/hooks/chatbot/ChatbotContext';
import Header from '~/components/Header';
import Footer from '~/components/Footer';
import useAppSetup from '~/hooks/useAppSetup';
import ErrorBoundary from '~/components/ErrorBoundary';
import Notifications from '~/components/Notifications';
import SFCONotifications from '~/components/SingleFormCheckout/Notifications';
import { Container } from '~/components/Layout/LayoutStyled';
import Loader from '~/components/Loader';
import 'react-tippy/dist/tippy.css';
import { logError, setMonarchMonitoring } from '@red-digital/bricks';
import { PAGE_ROUTES } from '~/constants/pages';
import listOfMonarchComponents from '../../monarch/listOfMonarchComponents';
import SessionModal from '~/components/Modal/screens/SessionExpired';
import '~/styles/scss/base.scss';
import '~/styles/bricks/all.scss';
import { withCohesion } from '../providers/CohesionProvider';
import Localize from '~/components/Localize/index';
import StickyBanner from '~/components/Banner/StickyBanner';
import Cohesion from '~/components/Cohesion';
import { spanishPreferred } from '~/helpers/spanishPreferred';
import MobileProgressBar from '~/components/SingleFormCheckout/MobileProgressBar';
import { useCSPViolationReporter } from '~/hooks/useCSPViolationReporter';
import useRouteNGCTracker from '~/hooks/useRouteNGCTracker';
import OutageContactForm from '~/components/OutageContactForm';
import { PageLayout } from '~/components/Layout/PageLayout';
import { ReduxState } from '~/types/redux';
import { evaluateRoute } from '~/guards/routeGuards';

const App: FC<AppProps> = ({ Component, pageProps }) => {
  const { store, production, router } = useAppSetup();
  const { pathname: currentPage } = router;
  const showHeaderAndFooter = currentPage !== PAGE_ROUTES.BUY_FIBER;
  const { c2f }: ReduxState = store?.getState() || {};
  const c2fEnabled = c2f?.enabled ?? false;
  const isOrderPage = currentPage === PAGE_ROUTES.BILLING_SUMMARY;
  const canShowMobileProgressBar = currentPage !== PAGE_ROUTES.ADDRESS_CHECK && !(isOrderPage && c2fEnabled);
  const [stickyBannerProps, setStickyBannerProps] = useState(null);
  const [isOutageHappening, setIsOutageHappening] = useState(false);

  function fuelProviderCallback({ isOutageHappening }: FuelProviderCallbackProps) {
    setIsOutageHappening(isOutageHappening);
  }

  useEffect(() => {
    setMonarchMonitoring({ hasUpdatedSnippet: true });

    window?.cohesion('error', function () {
      console.log('debug: Cohesion failed to load');
      logError('cohesion:failed: Cohesion failed to load');
    });

    window?.cohesion('monarch:done', function (err) {
      if (err) {
        console.error('Monarch evaluation failed', err);
        logError(`monarch:evaluation: failed - ${err}`);
        return;
      }
    });
  }, []);

  useLogRocket();
  useCSPViolationReporter();
  useRouteNGCTracker(store);

  const clearStickyBannerOnServiceCheck = (url: string) => {
    const pathWithoutQuery = url.split('?')[0].split('#')[0];
    if (pathWithoutQuery === PAGE_ROUTES.ADDRESS_CHECK) {
      setStickyBannerProps(null);
    }
  };

  // Sticky banner state is stored in _app and persists across route changes.
  // Ensure we always clear it when navigating to Service Check (/), including
  // cases where the route guard redirects before other listeners attach.
  useEffect(() => {
    if (!router) return;

    if (router.pathname === PAGE_ROUTES.ADDRESS_CHECK) {
      setStickyBannerProps(null);
    }

    router.events.on('routeChangeStart', clearStickyBannerOnServiceCheck);
    return () => {
      router.events.off('routeChangeStart', clearStickyBannerOnServiceCheck);
    };
  }, [router]);

  // Global route guard interceptor
  useEffect(() => {
    if (!store || !router) return;

    // Initial evaluation on first load/refresh
    const initial = evaluateRoute(router.pathname, store);
    if (!initial.allow && initial.redirectTo && router.pathname !== initial.redirectTo) {
      router.replace(initial.redirectTo);
      return; // don't attach listeners if we're redirecting immediately
    }

    const handleRouteChangeStart = (url: string) => {
      const res = evaluateRoute(url, store);
      if (!res.allow) {
        // Cancel the in-flight transition to avoid UI flicker
        router.events.emit('routeChangeError', new Error('route guard blocked navigation'));
        // Throw to abort navigation per Next.js recommendation
        throw new Error('Route guard blocked navigation');
      }
    };

    const handleRouteChangeComplete = (url: string) => {
      const res = evaluateRoute(url, store);
      if (!res.allow && res.redirectTo && router.pathname !== res.redirectTo) {
        router.replace(res.redirectTo);
      }
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router, store]);

  return (
    <FuelProvider store={store} production={production} router={router} callback={fuelProviderCallback}>
      <ChatbotProvider>
        <Head>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Head>
        <Cohesion />
        {spanishPreferred() && process.env.environment === 'production' && <Localize />}
        {stickyBannerProps && <StickyBanner {...stickyBannerProps} />}
        {!isOutageHappening && showHeaderAndFooter && <Header />}
        {canShowMobileProgressBar && <MobileProgressBar />}
        <ErrorBoundary production={production} number={store?.getState()?.callInNumber}>
          <Container>
            <SFCONotifications />
            <Notifications />
          </Container>
          {isOutageHappening ? (
            <PageLayout>
              <OutageContactForm />
            </PageLayout>
          ) : (
            <Component
              {...pageProps}
              stickyBannerProps={stickyBannerProps}
              setStickyBannerProps={setStickyBannerProps}
            />
          )}
          {!isOutageHappening && showHeaderAndFooter && <Footer />}
          <Loader />
        </ErrorBoundary>
        <SessionModal />
      </ChatbotProvider>
    </FuelProvider>
  );
};
App.displayName = 'App';

export default withCohesion({
  App,
  components: listOfMonarchComponents,
});
